/**
 * 页面资源预加载管理器
 */
class ResourcePreloader {
  constructor() {
    const domain = window.location.origin;

    // 记录已预加载的URLs和推测规则
    this.preloadHistory = new Set();
    this.speculationRules = new Set();

    // 页面预加载映射
    this.pageToPreload = {
      '/': [
        '/pages/aquasense-pro',
        '/pages/aquasense', 
        '/pages/iskim-ultra'
      ],
      '/pages/aquasense-pro': ['/products/aquasense-pro'],
      '/pages/aquasense': ['/products/aquasense'],
      '/pages/iskim-ultra': ['/products/iskim-ultra']
    };

    // 结账资源
    this.checkoutResources = {
      scripts: [
        `${domain}/cdn/shop/t/3/assets/checkout.js`,
        `${domain}/cdn/shop/t/3/assets/vendors.js`,
        `${domain}/cdn/shop/t/3/assets/runtime.js`,
        'https://js.stripe.com/v3/',
        'https://pay.google.com/gp/p/js/pay.js'
      ],
      styles: [
        `${domain}/cdn/shop/t/3/assets/checkout.css`,
        `${domain}/cdn/shop/t/3/assets/vendors.css`
      ],
      connections: [
        'https://js.stripe.com',
        'https://pay.google.com',
        'https://checkout.paypal.com'
      ]
    };

    this.init();
  }

  /**
   * 检查是否支持推测规则
   */
  checkSpeculationRulesSupport() {
    return HTMLScriptElement.supports?.('speculationrules') || false;
  }

  /**
   * 安全的 requestIdleCallback
   */
  safeRequestIdle(callback, options = {}) {
    if ('requestIdleCallback' in window) {
      return window.requestIdleCallback(callback, options);
    }
    return setTimeout(() => {
      callback({
        didTimeout: false,
        timeRemaining: () => 15
      });
    }, 1000);
  }

  /**
   * 等待页面加载完成
   */
  waitForPageLoad(callback) {
    let timeoutId;

    // 创建一个 MutationObserver 来监听 DOM 变化
    const observer = new MutationObserver((mutations, obs) => {
      // 检查页面是否已经加载完成
      if (document.readyState === 'complete') {
        obs.disconnect(); // 停止观察
        clearTimeout(timeoutId); // 清除超时定时器
        callback();
      }
    });

    // 开始观察 document 的变化
    observer.observe(document, {
      childList: true,
      subtree: true
    });

    // 设置超时，防止无限等待
    timeoutId = setTimeout(() => {
      observer.disconnect();
      callback();
    }, 20000); // 5秒超时

    // 如果页面已经加载完成，直接执行回调
    if (document.readyState === 'complete') {
      observer.disconnect();
      clearTimeout(timeoutId);
      callback();
    }
  }

  init() {
    // 初始页面加载
    this.handleRouteChange();

    // 监听路由变化
    window.addEventListener('popstate', () => {
      this.handleRouteChange();
    });
  }

  /**
   * 处理路由变化
   */
  handleRouteChange() {
    this.waitForPageLoad(() => {
      const currentPath = window.location.pathname;
      
      // 处理页面预加载
      const pagesToPreload = this.pageToPreload[currentPath];
      if (pagesToPreload) {
        this.addSpeculationRules(pagesToPreload);
      }

      // 在产品页面预加载结账资源
      if (currentPath.includes('/products/')) {
        this.preloadCheckoutResources();
      }
    });
  }

  /**
   * 添加推测规则
   */
  addSpeculationRules(urls) {
    if (!this.checkSpeculationRulesSupport() || !this.shouldPreload()) {
      return;
    }

    // 创建推测规则
    const rules = {
      prerender: [
        {
          source: 'list',
          urls: urls.filter(url => !this.speculationRules.has(url)),
          eagerness: 'immediate' // 立即预渲染
        }
      ]
    };

    // 如果没有新的URL需要添加，直接返回
    if (rules.prerender[0].urls.length === 0) {
      return;
    }

    // 添加推测规则
    const script = document.createElement('script');
    script.type = 'speculationrules';
    script.text = JSON.stringify(rules);
    document.head.appendChild(script);

    // 记录已添加的URL
    rules.prerender[0].urls.forEach(url => {
      this.speculationRules.add(url);
    });
  }

  /**
   * 预加载结账资源
   */
  preloadCheckoutResources() {
    if (!this.shouldPreload()) return;

    this.safeRequestIdle(() => {
      this.preconnectToDomains();
      this.preloadCheckoutAssets();
    }, { timeout: 2000 });
  }

  /**
   * 预连接到关键域名
   */
  preconnectToDomains() {
    this.checkoutResources.connections.forEach(domain => {
      // DNS 预解析
      const dns = document.createElement('link');
      dns.rel = 'dns-prefetch';
      dns.href = domain;
      document.head.appendChild(dns);

      // 预连接
      const preconnect = document.createElement('link');
      preconnect.rel = 'preconnect';
      preconnect.href = domain;
      preconnect.crossOrigin = 'anonymous';
      document.head.appendChild(preconnect);
    });
  }

  /**
   * 预加载结账资产
   */
  preloadCheckoutAssets() {
    // 预加载样式
    this.checkoutResources.styles.forEach(url => {
      if (this.preloadHistory.has(url)) return;
      
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'style';
      link.href = url;
      document.head.appendChild(link);
      this.preloadHistory.add(url);
    });

    // 预加载脚本
    this.checkoutResources.scripts.forEach(url => {
      if (this.preloadHistory.has(url)) return;
      
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'script';
      link.href = url;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
      this.preloadHistory.add(url);
    });
  }

  /**
   * 检查是否应该预加载
   */
  shouldPreload() {
    // 检查网络状态
    if ('connection' in navigator) {
      const connection = navigator.connection;
      if (connection.saveData || 
          connection.effectiveType === 'slow-2g' || 
          connection.effectiveType === '2g') {
        return false;
      }
    }
    return true;
  }
}

// 创建实例
const resourcePreloader = new ResourcePreloader();

// 监听网络状态变化
if ('connection' in navigator) {
  navigator.connection.addEventListener('change', () => {
    if (resourcePreloader.shouldPreload()) {
      resourcePreloader.handleRouteChange();
    }
  });
}